<template>
  <details class="toggle-block" :open="initiallyOpen" @toggle="onToggle">
    <summary class="toggle-header">
      <span class="toggle-chevron" :class="{ open: isOpen }" aria-hidden="true">
        <q-icon name="chevron_right" size="20px" />
      </span>
      <span class="toggle-title">{{ block.title }}</span>
      <span class="toggle-hint">{{ isOpen ? 'Ocultar' : 'Ver detalle' }}</span>
    </summary>
    <div class="toggle-content" v-html="renderedContent" />
  </details>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { renderMarkdown } from '../markdown';

interface ToggleBlock {
  type: 'toggle';
  title: string;
  content: string;
}

const props = defineProps<{ block: ToggleBlock }>();

const initiallyOpen = false;
const isOpen = ref(initiallyOpen);

const renderedContent = computed(() => renderMarkdown(props.block.content));

function onToggle(event: Event) {
  const target = event.target as HTMLDetailsElement;
  isOpen.value = target.open;
}
</script>

<style scoped lang="scss">
.toggle-block {
  border: 1px solid var(--oa-border);
  border-radius: var(--oa-radius-lg);
  margin: 20px 0;
  background: var(--oa-bg-2);
  overflow: hidden;
  transition: border-color var(--oa-duration-fast) ease;

  &[open] {
    border-color: color-mix(in srgb, var(--oa-gold-full) 28%, transparent);
  }
}

.toggle-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  cursor: pointer;
  list-style: none;
  user-select: none;
  transition: background var(--oa-duration-fast) ease;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    background: color-mix(in srgb, var(--oa-gold-full) 4%, transparent);
  }
}

.toggle-chevron {
  color: var(--oa-gold-full);
  transition: transform var(--oa-duration-fast) ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &.open {
    transform: rotate(90deg);
  }
}

.toggle-title {
  flex: 1;
  font-weight: 700;
  font-size: var(--oa-text-sm);
  color: var(--oa-text-primary);
  letter-spacing: 0.01em;
}

.toggle-hint {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.toggle-content {
  padding: 4px 18px 18px 48px;
  font-size: var(--oa-text-base);
  color: var(--oa-text-primary);
  line-height: 1.7;
  border-top: 1px solid color-mix(in srgb, var(--oa-border) 40%, transparent);
  margin-top: 0;
  padding-top: 14px;

  :deep(p) {
    margin: 0 0 10px 0;
    &:last-child { margin-bottom: 0; }
  }

  :deep(strong) {
    color: var(--oa-text-primary);
    font-weight: 700;
  }

  :deep(em) { font-style: italic; }

  :deep(a) {
    color: var(--oa-gold-full);
    text-decoration: underline;
  }

  :deep(ul), :deep(ol) {
    margin: 8px 0;
    padding-left: 22px;
  }

  :deep(li) {
    margin-bottom: 4px;
  }

  :deep(li::marker) {
    color: var(--oa-gold-full);
  }

  :deep(code) {
    background: var(--oa-bg-3);
    padding: 1px 5px;
    border-radius: var(--oa-radius-sm);
    font-family: var(--oa-font-mono);
    font-size: 0.875em;
  }

  :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
    font-size: var(--oa-text-sm);
    background: var(--oa-bg-1);
    border-radius: var(--oa-radius-md);
    overflow: hidden;
  }

  :deep(th) {
    text-align: left;
    padding: 8px 12px;
    font-weight: 700;
    color: var(--oa-gold-full);
    background: color-mix(in srgb, var(--oa-gold-full) 6%, transparent);
    border-bottom: 1px solid color-mix(in srgb, var(--oa-gold-full) 18%, transparent);
    font-size: var(--oa-text-xs);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  :deep(td) {
    padding: 8px 12px;
    border-bottom: 1px solid color-mix(in srgb, var(--oa-border) 30%, transparent);
  }

  :deep(tr:last-child td) {
    border-bottom: none;
  }
}
</style>
